import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import { ContentTypeFieldResponse } from '@cs-cms/types';
import { MASTER_FIELD_PREFIX, RT_MASTER_EMPTY_PLACEHOLDER } from '../model/masterFieldConstants';

export interface RtMasterFieldBehavior {
  shouldHideField?: (field: ContentTypeFieldResponse) => boolean;
  canEditReadOnlyField?: (field: ContentTypeFieldResponse, allFields: ContentTypeFieldResponse[]) => boolean;
  isFieldInEditMode?: (fieldName: string) => boolean;
  onStartEdit?: (args: {
    fieldName: string;
    allFields: ContentTypeFieldResponse[];
    formRenderProps: FormRenderProps;
  }) => void;
  onSaveEdit?: (args: {
    fieldName: string;
    allFields: ContentTypeFieldResponse[];
    formRenderProps: FormRenderProps;
    runLookupForField: (
      lookupFieldName: string,
      lookupValue: unknown,
      lookupFormRenderProps: FormRenderProps
    ) => Promise<{ valuesForOtherFields?: Record<string, unknown> } | null>;
  }) => Promise<void>;
  onCancelEdit?: (args: {
    fieldName: string;
    allFields: ContentTypeFieldResponse[];
    formRenderProps: FormRenderProps;
  }) => void;
}

const getMasterFieldName = (fieldName: string): string => {
  return `${MASTER_FIELD_PREFIX}${fieldName}`;
};

const isMasterField = (fieldName: string): boolean => {
  return fieldName.startsWith(MASTER_FIELD_PREFIX);
};

const hasMasterField = (fieldName: string, allFields: ContentTypeFieldResponse[]): boolean => {
  const masterFieldName = getMasterFieldName(fieldName);
  return allFields.some(field => field.name === masterFieldName);
};

const getEditableFieldNames = (allFields: ContentTypeFieldResponse[]): Set<string> => {
  const fieldNames = new Set(allFields.map(field => field.name));
  const editableFieldNames = new Set<string>();

  allFields.forEach(field => {
    if (!isMasterField(field.name)) {
      return;
    }

    const originalFieldName = field.name.slice(MASTER_FIELD_PREFIX.length);
    if (fieldNames.has(originalFieldName)) {
      editableFieldNames.add(originalFieldName);
    }
  });

  return editableFieldNames;
};

const getDependentFieldNames = (fieldName: string, allFields: ContentTypeFieldResponse[]): string[] => {
  return allFields
    .filter(field => field.name !== fieldName && field.dependsOnFields?.includes(fieldName))
    .map(field => field.name);
};

const setFieldValue = (formRenderProps: FormRenderProps, fieldName: string, value: unknown): void => {
  formRenderProps.onChange(fieldName, { value });
};

const getFallbackCancelledValue = (): string => {
  return '';
};

const isValueEmpty = (value: unknown): boolean => {
  return value == null || value === '';
};

export const useRtMasterFieldBehavior = (): RtMasterFieldBehavior => {
  const [fieldsInEditMode, setFieldsInEditMode] = React.useState<Record<string, boolean>>({});
  const [fieldValuesBeforeEdit, setFieldValuesBeforeEdit] = React.useState<Record<string, unknown>>({});

  return React.useMemo<RtMasterFieldBehavior>(() => {
    return {
      shouldHideField: field => {
        return field.technical === true;
      },
      canEditReadOnlyField: (field, allFields) => {
        const editableFieldNames = getEditableFieldNames(allFields);
        return editableFieldNames.has(field.name);
      },
      isFieldInEditMode: fieldName => {
        return fieldsInEditMode[fieldName] === true;
      },
      onStartEdit: ({ fieldName, allFields, formRenderProps }) => {
        const currentFieldValue = formRenderProps.valueGetter(fieldName);
        const masterFieldName = getMasterFieldName(fieldName);
        const currentMasterValue = formRenderProps.valueGetter(masterFieldName);
        const isMasterEnabledField = hasMasterField(fieldName, allFields);

        setFieldValuesBeforeEdit(prev => ({
          ...prev,
          [fieldName]: currentFieldValue
        }));
        setFieldsInEditMode(prev => ({
          ...prev,
          [fieldName]: true
        }));

        if (isMasterEnabledField && isValueEmpty(currentMasterValue)) {
          setFieldValue(
            formRenderProps,
            masterFieldName,
            isValueEmpty(currentFieldValue) ? RT_MASTER_EMPTY_PLACEHOLDER : currentFieldValue
          );
        }
      },
      onSaveEdit: async ({ fieldName, allFields, formRenderProps, runLookupForField }) => {
        const currentValue = formRenderProps.valueGetter(fieldName);
        const lookupResponse = await runLookupForField(fieldName, currentValue, formRenderProps);
        const valuesForOtherFields = lookupResponse?.valuesForOtherFields ?? {};
        const responseKeys = Object.keys(valuesForOtherFields);
        const dependentFieldNames = getDependentFieldNames(fieldName, allFields).filter(
          dependentFieldName => !isMasterField(dependentFieldName)
        );

        if (responseKeys.length === 0) {
          dependentFieldNames.forEach(dependentFieldName => {
            setFieldValue(formRenderProps, dependentFieldName, null);
            if (hasMasterField(dependentFieldName, allFields)) {
              setFieldValue(formRenderProps, getMasterFieldName(dependentFieldName), RT_MASTER_EMPTY_PLACEHOLDER);
            }
          });
        }

        setFieldsInEditMode(prev => ({
          ...prev,
          [fieldName]: false
        }));
        setFieldValuesBeforeEdit(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      },
      onCancelEdit: ({ fieldName, formRenderProps }) => {
        const valueBeforeEdit = fieldValuesBeforeEdit[fieldName];
        const restoredValue = valueBeforeEdit === undefined ? getFallbackCancelledValue() : valueBeforeEdit;

        setFieldValue(formRenderProps, fieldName, restoredValue);
        setFieldsInEditMode(prev => ({
          ...prev,
          [fieldName]: false
        }));
        setFieldValuesBeforeEdit(prev => {
          const updated = { ...prev };
          delete updated[fieldName];
          return updated;
        });
      }
    };
  }, [fieldValuesBeforeEdit, fieldsInEditMode]);
};
